/*
 * -----------------------------------------------------------------------
 *  <copyright file="edit-visit-panel/index.tsx" company="Triumph Research Intelligence">
 *      Copyright (C) 2023 by Triumph Research Intelligence. All rights reserved.
 *  </copyright>
 * -----------------------------------------------------------------------
 */

import React from 'react';
import { UseBoolean } from 'react-hanger';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import AddVisitForm from '@components/admin/add-visit-form';
import AssessmentsSVTGrid from '@components/admin/assessments-svt-grid';
import DarkTooltip from '@components/core/dark-tool-tip/dark-tool-tip';
import HeadingActions from '@components/core/heading-actions';
import OpraTabs from '@components/core/opra-tabs';
import { IOpraTab } from '@components/core/opra-tabs/opra-tabs.interface';
import RefreshComponent from '@components/core/refresh-component';
import CRFsSVTGrid from 'views/components/admin/crfs-svt-grid';
import { useTranslation } from '@hooks/index';
import { StudySubjectVisitFields } from '@queries/generated/fragments.generated';

import './styles.scss';

interface EditVisitPanelInterface {
    canEdit: boolean;
    openDeleteVisit: Function;
    visits: StudySubjectVisitFields[] | null;
    currentScheduleId: string | null;
    currentScheduleVersion?: string | null;
    studyId: string;
    currentVisit?: StudySubjectVisitFields | null;
    formChanged: UseBoolean;
    assessmentAssociation?: boolean | null;
}

const EditVisitPanel = ({
    canEdit = true,
    visits,
    openDeleteVisit,
    currentVisit,
    currentScheduleId,
    currentScheduleVersion,
    studyId,
    formChanged,
    assessmentAssociation,
}: EditVisitPanelInterface) => {
    const { t } = useTranslation();

    const tabsData: IOpraTab[] = [
        {
            tabTitle: t('subject:details'),
            tabContent: (
                <AddVisitForm
                    visits={visits}
                    currentVisit={currentVisit}
                    currentScheduleId={currentScheduleId}
                    studyId={studyId}
                    formChanged={formChanged}
                    canEdit={canEdit}
                />
            ),
        },
        {
            tabTitle: t('admin:CRFs'),
            tabContent: (
                <RefreshComponent dataChange={currentVisit?.id}>
                    <CRFsSVTGrid
                        studyId={studyId}
                        canEdit={canEdit}
                        currentVisit={currentVisit}
                        currentScheduleVersion={currentScheduleVersion}
                        currentScheduleId={currentScheduleId}
                        assessmentAssociation={assessmentAssociation}
                    />
                </RefreshComponent>
            ),
        },
        {
            tabTitle: t('subject:assessments'),
            tabContent: (
                <RefreshComponent dataChange={currentVisit?.id}>
                    <AssessmentsSVTGrid
                        studyId={studyId}
                        canEdit={canEdit}
                        currentVisit={currentVisit}
                        currentScheduleVersion={currentScheduleVersion}
                        currentScheduleId={currentScheduleId}
                        assessmentAssociation={assessmentAssociation}
                        className="tab-grid"
                    />
                </RefreshComponent>
            ),
        },
    ];

    return (
        <div className="edit-visit-panel">
            <div className="visit-panel-header">
                <div className="title">{t('admin:editVisit')}</div>
                <HeadingActions
                    items={[
                        {
                            element: (
                                <DarkTooltip title={t('admin:deleteVisit')} placement="top" arrow>
                                    <FontAwesomeIcon icon={['fal', 'trash']} className="fa-18" />
                                </DarkTooltip>
                            ),
                            action: openDeleteVisit,
                            disabled: !canEdit,
                            'data-testid': 'delete-button',
                        },
                    ]}
                />
            </div>
            <OpraTabs data={tabsData} disableClick={formChanged} />
        </div>
    );
};

export default EditVisitPanel;
